import {
  DDIProvision,
  Product,
  ServiceCharge,
  ServiceChargePost,
} from "callable-data-objects";
import useCheckRole from "Hooks/APICheckRole";
import { useTicket } from "Hooks/Endpoints";
import { isEmpty } from "lodash";
import moment from "moment";
import React, { Fragment } from "react";
import {
  Button,
  Checkbox,
  Dimmer,
  Label,
  Loader,
  Menu,
  Segment,
  Table,
} from "semantic-ui-react";

const cleanNumbers = (ddiText: string) => {
  let txt = ddiText.match(/[0-9]{9,20}/g);
  if (txt != null && !isEmpty(txt)) {
    return txt.map((value) => {
      let s = value;
      if (s.startsWith("00")) s = value.substr(2);
      if (s.startsWith("0")) s = "44" + value.substr(1);
      return s;
    });
  }
  return [];
};

const ExportBuildForm = () => {
  const { hasRole, roles } = useCheckRole();

  const {
    ticket,
    buildExport,
    loading,
    charges,
    ticketProductTable,
    addCharge,
    removeCharge,
  } = useTicket();

  const {
    ddi,
    associatedNumbers,
    requiredDate,
    portType,
    customer,
    ddis = [],
  } = ticket.exportRequest;

  const canExport = (): boolean => {
    const days = moment().diff(moment(requiredDate), "days");
    return days > 7;
  };

  const getExportableDDIs = (): string[] => {
    return cleanNumbers(ddi + "\n" + associatedNumbers);
  };

  const getCharge = (s: string): ServiceCharge => {
    return (
      charges &&
      charges.find(
        (sc: ServiceCharge) => sc.reference === s && sc.name === portType
      )
    );
  };

  const getProduct = () => {
    return (
      ticketProductTable &&
      ticketProductTable.products.find((p: Product) => p.name === portType)
    );
  };

  const add = (s: string) => {
    const product = getProduct();
    if (product) {
      const charge: ServiceChargePost = {
        customer: customer,
        reference: s,
        name: product.name,
        type: product.category,
        description: product.description,
        ticketReference: ticket.caseId,
        unit: product.type,
        price: product.price,
        qty: 1,
        total: product.price,
        start: moment().toISOString(),
      };
      addCharge(charge);
    }
  };

  const remove = (s: string) => {
    const c: ServiceCharge = getCharge(s);
    if (c) {
      removeCharge(c.id);
    }
  };

  return (
    <Fragment>
      <Dimmer inverted active={loading}>
        <Loader inverted />
      </Dimmer>
      {hasRole(roles.ticket.build.view) && (
        <Segment>
          <Menu>
            <Menu.Item header>Provisioning</Menu.Item>
            {hasRole(roles.ticket.build.build) && (
              <Menu.Menu position='right'>
                <Menu.Item>
                  <Button
                    secondary
                    onClick={buildExport}
                    disabled={!canExport()}
                  >
                    Export
                  </Button>
                </Menu.Item>
              </Menu.Menu>
            )}
          </Menu>
          <Table compact stackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>DDI</Table.HeaderCell>
                <Table.HeaderCell>Exported</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {getExportableDDIs().map((s: string) => (
                <Table.Row>
                  <Table.Cell width={14}>{s}</Table.Cell>
                  <Table.Cell width={2}>
                    <Checkbox
                      toggle
                      checked={
                        ddis &&
                        ddis.find((dp: DDIProvision) => dp.ddi.name === s) !==
                          undefined
                      }
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      )}
      {hasRole(roles.ticket.billing.view) && (
        <Segment>
          <Menu>
            <Menu.Item header>
              Billing <Label as='tag'>{ticketProductTable?.name}</Label>
            </Menu.Item>
          </Menu>
          <Table compact stackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>DDI</Table.HeaderCell>
                <Table.HeaderCell>Product Name</Table.HeaderCell>
                <Table.HeaderCell>Product Price</Table.HeaderCell>
                {hasRole(roles.ticket.billing.build) && (
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                )}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {getExportableDDIs().map((s: string) => (
                <Table.Row>
                  <Table.Cell width={6}>{s}</Table.Cell>
                  <Table.Cell width={6}>{portType}</Table.Cell>
                  <Table.Cell>
                    {ticketProductTable &&
                      ticketProductTable.products.find(
                        (p: Product) => p.name === portType
                      )?.price}
                  </Table.Cell>
                  {hasRole(roles.ticket.billing.build) && (
                    <Table.Cell>
                      {getCharge(s) === undefined ? (
                        <Button secondary onClick={() => add(s)}>
                          Add
                        </Button>
                      ) : (
                        <Button primary onClick={() => remove(s)}>
                          Remove
                        </Button>
                      )}
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Segment>
      )}
    </Fragment>
  );
};

export default ExportBuildForm;
